import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService, getErrorMessage } from '@/services';
import { useAuthStore } from '@/store/auth';
import type { LoginRequest, AdminLoginResponse } from '@/types';
import { INSURER_HOME, INSURER_LOGIN_PATH, isInsurerRole } from '@/routing/portalHome';

export function useLogin() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (data: AdminLoginResponse) => {
      const countryContext = data.country ?? null;
      login(data.user, data.access, undefined, undefined, countryContext);
      toast.success('Login successful');
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useInsurerLogin() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.insurerLogin(credentials),
    onSuccess: (data) => {
      login(data.user, data.access, 'insurer', data.insurer);
      toast.success(`Welcome to ${data.insurer.name}`);
      // #350: the insurer portal's home is its own dashboard.
      navigate(INSURER_HOME);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useBrokerLogin() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.brokerLogin(credentials),
    onSuccess: (data) => {
      login(data.user, data.access, 'broker', data.broker);
      toast.success(`Welcome to ${data.broker.name}`);
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useChannelLogin() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.channelLogin(credentials),
    onSuccess: (data) => {
      login(data.user, data.access, 'channel', data.channel);
      toast.success('Login successful');
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useCustomerLogin() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.customerLogin(credentials),
    onSuccess: (data) => {
      login(data.user, data.access, 'customer', data.customer);
      toast.success('Welcome back!');
      navigate('/dashboard');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    // 9c: the refresh token is an httpOnly cookie, so the sign-out call always
    // goes out -- it is the only thing that can blacklist the token and clear
    // the cookie, and this bundle has no way to check whether one is there.
    mutationFn: () => authService.logout(),
    onSettled: () => {
      // #366: insurer staff sign in at /login/insurer, so that is where
      // signing out returns them. Read the role BEFORE logout() clears it.
      // Every other role still lands on /login.
      const signInPage = isInsurerRole(user?.role) ? INSURER_LOGIN_PATH : '/login';
      logout();
      queryClient.clear();
      navigate(signInPage);
      toast.success('Logged out successfully');
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated, setUser } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const user = await authService.getCurrentUser();
      setUser(user);
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: authService.updateProfile,
    onSuccess: (data) => {
      setUser(data);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { old_password: string; new_password: string; new_password_confirm: string }) =>
      authService.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
